<div class="config-hub-container fade-in">
  <!-- Header Section -->
  <header class="config-hub-header">
    <div class="header-left">
      <button class="back-btn" (click)="goBack()">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back to Dashboard
      </button>

      <div class="title-meta">
        <div>
          <h2>{{ isGlobal ? 'Global Configuration' : (deviceName || deviceId) + ' Configuration' }}</h2>
          <div class="meta-badges-row">
            <span class="meta-badge" [class.global-badge]="isGlobal" [class.device-badge]="!isGlobal">
              {{ isGlobal ? 'Global' : 'Device: ' + deviceId }}
            </span>
            <span *ngIf="!isGlobal && storeId" class="meta-badge store-badge">
              Store: {{ storeId }}
            </span>
            <span *ngIf="!isGlobal && overrideGlobalLayout" class="custom-status-badge standalone-badge">
              ✨ Standalone Layout
            </span>
            <span *ngIf="!isGlobal && !overrideGlobalLayout && isCustomized" class="custom-status-badge">
              ✨ Customized Tiles
            </span>
            <span *ngIf="!isGlobal && !overrideGlobalLayout && !isCustomized" class="global-default-badge">
              🌐 Global Default Layout
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="header-right">
      <button class="success-btn publish-device-btn" *ngIf="!isGlobal && !version" (click)="publishToDevice()" [disabled]="isPublishingDevice">
        <span class="spinner-inline" *ngIf="isPublishingDevice"></span>
        <span *ngIf="!isPublishingDevice">🚀</span>
        <span>{{ isPublishingDevice ? 'Publishing...' : 'Publish to Device' }}</span>
      </button>

      <button class="primary-btn launch-editor-btn" (click)="openLayoutEditor()">
        <span>📐</span>
        <span>{{ version ? 'View Layout in Editor' : 'Launch Layout Editor' }}</span>
      </button>
    </div>
  </header>

  <!-- Read-Only Banner -->
  <div class="read-only-banner" *ngIf="version">
    <span>⚠️</span>
    <span><strong>Viewing Historical Version ({{ version }}):</strong> Configuration is in read-only mode.</span>
  </div>

  <!-- Loading State -->
  <div class="loading-overlay-container" *ngIf="isLoading">
    <div class="spinner"></div>
    <p class="loading-text">Loading configuration details...</p>
  </div>

  <!-- Main Config Content (Clean Sectioned Form Panel) -->
  <div class="config-hub-body" *ngIf="!isLoading">
    <div class="config-form-container">
      
      <!-- ============================================== -->
      <!-- SECTION 1: GENERAL & DEVICE SETTINGS -->
      <!-- ============================================== -->
      <div class="form-section" *ngIf="!isGlobal">
        <div class="section-header">
          <div class="section-title-wrap">
            <span class="section-icon">⚙️</span>
            <h3>General & Device Settings</h3>
          </div>
          <p class="section-desc">Machine identity, hardware connectivity status, and operational flags</p>
        </div>

        <div class="section-content">
          <!-- Row: Device ID (Read-only) -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Device ID</span>
              <span class="form-sublabel">Permanent hardware terminal identifier (immutable)</span>
            </div>
            <div class="form-value-col">
              <span class="value-highlight id-mono-text">{{ deviceId }}</span>
            </div>
          </div>

          <!-- Row: Device Name (Editable) -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Device Name</span>
              <span class="form-sublabel">Human-readable name assigned to this kiosk</span>
            </div>
            <div class="form-value-col device-name-input-group">
              <input type="text" class="custom-input name-input" [(ngModel)]="deviceName" [disabled]="!!version || isSavingDeviceName" placeholder="Enter device name">
              <button class="primary-btn btn-xs" *ngIf="deviceName !== originalDeviceName && !version" (click)="saveDeviceName()" [disabled]="isSavingDeviceName">
                <span *ngIf="!isSavingDeviceName">Save Name</span>
                <span *ngIf="isSavingDeviceName" class="spinner-inline"></span>
              </button>
            </div>
          </div>

          <!-- Row: App Version & Last Ping -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">App Version & Connectivity</span>
              <span class="form-sublabel">Telemetry received from the kiosk service</span>
            </div>
            <div class="form-value-col status-badges-group">
              <span class="app-version-badge" *ngIf="appVersion">v{{ appVersion }}</span>
              <span class="status-pill" [class.online]="deviceOnline" [class.offline]="!deviceOnline">
                {{ deviceOnline ? '🟢 Online' : '🔴 Offline' }}
              </span>
              <span class="status-pill" [class.online]="cameraConnected" [class.offline]="!cameraConnected">
                📷 {{ cameraConnected ? 'Camera Connected' : 'Camera Disconnected' }}
              </span>
              <span class="status-pill" [class.online]="cardMachineConnected" [class.offline]="!cardMachineConnected" *ngIf="useCard">
                💳 {{ cardMachineConnected ? 'Card OK' : 'Card Disconnected' }}
              </span>
              <span class="status-pill" [class.online]="billAcceptorConnected" [class.offline]="!billAcceptorConnected" *ngIf="useCash">
                💵 {{ billAcceptorConnected ? 'Cash OK' : 'Cash Disconnected' }}
              </span>
              <span class="value-timestamp" *ngIf="lastPinged">
                Last ping: {{ lastPinged | date:'short' }}
              </span>
            </div>
          </div>

          <!-- Row: Test Machine Switch -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Test Machine Mode</span>
              <span class="form-sublabel">Excludes this machine from live production and financial reports</span>
            </div>
            <div class="form-value-col toggle-action-wrapper">
              <label class="custom-toggle">
                <input type="checkbox" [(ngModel)]="isTesting" (change)="saveIsTesting()" [disabled]="!!version">
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status-text">{{ isTesting ? 'Enabled' : 'Disabled' }}</span>
              <span *ngIf="isTestingSaved" class="save-success-badge">✓ Saved</span>
            </div>
          </div>

          <!-- Row: Store Assignment -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Store Assignment</span>
              <span class="form-sublabel">Physical store branch linked to this kiosk terminal</span>
            </div>
            <div class="form-value-col store-select-group">
              <select class="custom-select" [(ngModel)]="selectedStoreId" [disabled]="!!version || isSavingStore">
                <option *ngFor="let s of allActiveStores" [value]="s.storeId">{{ s.name }} ({{ s.storeId }})</option>
              </select>
              <button class="primary-btn btn-xs" *ngIf="selectedStoreId !== storeId && !version" (click)="saveStore()" [disabled]="isSavingStore">
                <span *ngIf="!isSavingStore">Save Changes</span>
                <span *ngIf="isSavingStore" class="spinner-inline"></span>
              </button>
            </div>
          </div>

          <!-- Row: Layout Customization Status (Legacy / Quick Revert) -->
          <div class="form-row" *ngIf="isCustomized && !overrideGlobalLayout && !version">
            <div class="form-label-col">
              <span class="form-label">Customized Tiles</span>
              <span class="form-sublabel">Device has overrides on specific customizable tiles</span>
            </div>
            <div class="form-value-col">
              <button class="danger-btn btn-xs" (click)="revertToGlobal()">
                ↩ Reset Customized Tiles to Global
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================== -->
      <!-- SECTION 2: LAYOUT INHERITANCE & STANDALONE MODE -->
      <!-- ============================================== -->
      <div class="form-section" *ngIf="!isGlobal">
        <div class="section-header">
          <div class="section-title-wrap">
            <span class="section-icon">📐</span>
            <h3>Layout Mode & Canvas Control</h3>
          </div>
          <p class="section-desc">Choose whether this machine inherits global fleet layouts or operates as an independent custom canvas</p>
        </div>

        <div class="section-content">
          <!-- Layout Mode Selection Cards -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Layout Operation Mode</span>
              <span class="form-sublabel">Select inheritance behavior for this photobooth</span>
            </div>
            <div class="form-value-col mode-cards-grid">
              <!-- Mode Card 1: Global Inheritance -->
              <div class="mode-card" [class.active]="!overrideGlobalLayout" (click)="handleLayoutModeChange('global')">
                <div class="mode-card-header">
                  <div class="mode-radio-indicator">
                    <div class="radio-dot" *ngIf="!overrideGlobalLayout"></div>
                  </div>
                  <div class="mode-title-wrap">
                    <span class="mode-title">🌐 Inherit Global Layout</span>
                    <span class="mode-badge inherited-badge">Standard Mode</span>
                  </div>
                </div>
                <p class="mode-description">
                  Inherits fleet-wide layout, button positions, and global themes. Only tiles explicitly marked as customizable by global admins can be modified.
                </p>
              </div>

              <!-- Mode Card 2: Standalone Device Layout -->
              <div class="mode-card" [class.active]="overrideGlobalLayout" (click)="handleLayoutModeChange('standalone')">
                <div class="mode-card-header">
                  <div class="mode-radio-indicator">
                    <div class="radio-dot" *ngIf="overrideGlobalLayout"></div>
                  </div>
                  <div class="mode-title-wrap">
                    <span class="mode-title">✨ Standalone Device Layout</span>
                    <span class="mode-badge standalone-badge">Full Custom</span>
                  </div>
                </div>
                <p class="mode-description">
                  Completely detached from the global layout. Unlocks free-form tile dragging, resizing, adding new tiles, deleting tiles, uploading custom backgrounds, and custom themes for this machine only.
                </p>
              </div>
            </div>
          </div>

          <!-- Summary and Clone Actions for Standalone -->
          <div class="form-row" *ngIf="overrideGlobalLayout && !version">
            <div class="form-label-col">
              <span class="form-label">Clone / Template Actions</span>
              <span class="form-sublabel">Duplicate layout coordinates and artwork assets from another machine or global</span>
            </div>
            <div class="form-value-col">
              <button class="secondary-btn btn-sm clone-btn" (click)="openCloneModal()">
                <span>📋</span>
                <span>Clone Layout from Another Machine / Global</span>
              </button>
            </div>
          </div>

          <!-- Row: Revert to Global (if customized or standalone) -->
          <div class="form-row" *ngIf="(isCustomized || overrideGlobalLayout) && !version">
            <div class="form-label-col">
              <span class="form-label">Revert Configuration</span>
              <span class="form-sublabel">Permanently purge all custom device layout overrides and restore global fleet inheritance</span>
            </div>
            <div class="form-value-col">
              <button class="danger-btn btn-sm" (click)="revertToGlobal()">
                <span>↩</span>
                <span>Revert to Global Layout Baseline</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================== -->
      <!-- SECTION: MACHINE STATUS EMAIL NOTIFICATIONS (Global) -->
      <!-- ============================================== -->
      <div class="form-section" *ngIf="isGlobal">
        <div class="section-header">
          <div class="section-title-wrap">
            <span class="section-icon">📧</span>
            <h3>Machine Status Email Notifications</h3>
          </div>
          <p class="section-desc">Automated daily diagnostic report delivering peripheral health summaries to technician teams</p>
        </div>

        <div class="section-content">
          <!-- Row: Master Switch -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Send Machine Status Email</span>
              <span class="form-sublabel">Enable automated scheduled email reports covering all fleet terminals</span>
            </div>
            <div class="form-value-col toggle-action-wrapper">
              <label class="custom-toggle">
                <input type="checkbox" [(ngModel)]="sendMachineStatusEmail" (change)="toggleSendMachineStatusEmail()" [disabled]="!!version">
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status-text">{{ sendMachineStatusEmail ? 'Enabled' : 'Disabled' }}</span>
              <span *ngIf="isEmailSettingsSaved" class="save-success-badge">✓ Saved</span>
            </div>
          </div>

          <!-- Conditional settings when switch is ON -->
          <ng-container *ngIf="sendMachineStatusEmail">
            <!-- Row: Recipient Email Addresses -->
            <div class="form-row">
              <div class="form-label-col">
                <span class="form-label">Recipient Email Addresses</span>
                <span class="form-sublabel">List of destination emails separated by commas</span>
              </div>
              <div class="form-value-col email-input-col">
                <input type="text"
                  class="custom-input email-recipients-input"
                  [(ngModel)]="machineStatusEmailRecipients"
                  placeholder="admin@kceg.com, tech-team@kceg.com"
                  [disabled]="!!version">
                <span class="input-helper-text">Multiple recipients can be separated by commas or semicolons.</span>
              </div>
            </div>

            <!-- Row: Delivery Time (PST) -->
            <div class="form-row">
              <div class="form-label-col">
                <span class="form-label">Delivery Time (PST)</span>
                <span class="form-sublabel">Pacific Standard Time hour for daily automated delivery</span>
              </div>
              <div class="form-value-col hour-select-col">
                <select class="custom-select hour-dropdown-select" [(ngModel)]="machineStatusEmailHourPT" [disabled]="!!version">
                  <option *ngFor="let item of pstHoursList" [value]="item.hour">{{ item.label }}</option>
                </select>
                <span class="input-helper-text">Cloud Function checks every hour and dispatches at the chosen PT time.</span>
              </div>
            </div>

            <!-- Row: Actions (Save & Quick Test) -->
            <div class="form-row actions-row">
              <div class="form-label-col">
                <span class="form-label">Save &amp; Test Dispatch</span>
                <span class="form-sublabel">Persist configuration or send a diagnostic email immediately</span>
              </div>
              <div class="form-value-col">
                <div class="email-actions-group">
                  <button type="button" class="primary-btn btn-sm" (click)="saveEmailSettings(true)" [disabled]="!!version || isSavingEmailSettings">
                    <span *ngIf="isSavingEmailSettings" class="spinner-inline"></span>
                    <span>Save Settings</span>
                  </button>

                  <button type="button" class="action-test-btn btn-sm" (click)="sendQuickTestEmail()" [disabled]="isSendingTestEmail">
                    <span *ngIf="isSendingTestEmail" class="spinner-inline"></span>
                    <span *ngIf="!isSendingTestEmail">⚡</span>
                    <span>{{ isSendingTestEmail ? 'Sending Test Email...' : 'Send Quick Test' }}</span>
                  </button>
                </div>

                <!-- Feedback Toast/Message -->
                <div class="email-feedback-banner" *ngIf="testEmailFeedback" [class.feedback-success]="testEmailStatus === 'success'" [class.feedback-error]="testEmailStatus === 'error'">
                  <span>{{ testEmailFeedback }}</span>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- ============================================== -->
      <!-- SECTION 2: MEDIA & VIDEO MANAGEMENT -->
      <!-- ============================================== -->
      <div class="form-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <span class="section-icon">🎬</span>
            <h3>Media & Video Configuration</h3>
          </div>
          <p class="section-desc">Screensaver video loops, printing video assets, and screen background artworks</p>
        </div>

        <div class="section-content">
          <!-- Row: Exclude Global Videos (Device only) -->
          <div class="form-row exclusion-row" *ngIf="!isGlobal">
            <div class="form-label-col">
              <span class="form-label">Global Video Playback</span>
              <span class="form-sublabel">Exclude global videos from playing on this kiosk terminal</span>
            </div>
            <div class="form-value-col toggle-action-wrapper">
              <label class="custom-toggle">
                <input type="checkbox" [checked]="excludeGlobalVideos" (change)="toggleExcludeGlobalVideos()" [disabled]="!!version || isUpdatingExcludeGlobalVideos">
                <span class="toggle-slider"></span>
              </label>
              <span class="toggle-status-text">
                {{ excludeGlobalVideos ? 'Do not play global videos (Device-only)' : 'Play global + device videos' }}
              </span>
              <span *ngIf="isUpdatingExcludeGlobalVideos" class="spinner-inline"></span>
            </div>
          </div>

          <!-- Row: Screensaver Intro Videos -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Screensaver Intro Videos</span>
              <span class="form-sublabel">MP4 video loops played sequentially when the kiosk is idle</span>
            </div>
            <div class="form-value-col media-col">
              <div class="media-actions-bar" *ngIf="!version">
                <label class="primary-btn btn-xs upload-btn-label">
                  <span *ngIf="!isUploadingVideo">+ Upload Intro MP4</span>
                  <span *ngIf="isUploadingVideo">Uploading... ⏳</span>
                  <input type="file" accept="video/mp4" (change)="uploadIntroVideo($event)" class="hidden-file-input" [disabled]="isUploadingVideo">
                </label>
              </div>

              <div class="video-list" *ngIf="introVideos.length > 0">
                <div *ngFor="let vid of introVideos" class="video-item">
                  <span class="video-name">🎬 {{ extractVideoName(vid) }}</span>
                  <button *ngIf="!version" class="icon-action-btn danger btn-xs" (click)="deleteVideo('intro', vid)" [disabled]="isUploadingVideo" title="Delete Video">🗑️</button>
                </div>
              </div>
              <p *ngIf="introVideos.length === 0" class="no-items-text">No intro screensaver videos uploaded.</p>
            </div>
          </div>

          <!-- Row: Printing Videos & Coupled Backgrounds -->
          <div class="form-row">
            <div class="form-label-col">
              <span class="form-label">Printing Screen Videos & Backdrops</span>
              <span class="form-sublabel">MP4 videos played during printing with optional coupled background images (`_PBG`)</span>
            </div>
            <div class="form-value-col media-col">
              <div class="media-actions-bar" *ngIf="!version">
                <label class="primary-btn btn-xs upload-btn-label">
                  <span *ngIf="!isUploadingVideo">+ Upload Printing MP4</span>
                  <span *ngIf="isUploadingVideo">Uploading... ⏳</span>
                  <input type="file" accept="video/mp4" (change)="uploadPrintingMedia($event)" class="hidden-file-input" [disabled]="isUploadingVideo">
                </label>
              </div>

              <div class="video-list" *ngIf="printingVideos.length > 0">
                <div *ngFor="let vid of printingVideos" class="printing-media-card">
                  <div class="media-main-row">
                    <div class="video-info-cell">
                      <span class="video-icon">🖨️</span>
                      <span class="video-name">{{ extractVideoName(vid) }}</span>
                    </div>
                    <div class="video-actions-cell" *ngIf="!version">
                      <button class="icon-action-btn danger btn-xs" (click)="deleteVideo('printing', vid)" [disabled]="isUploadingVideo" title="Delete Printing Video">🗑️</button>
                    </div>
                  </div>

                  <!-- Coupled Background Sub-Row -->
                  <div class="media-bg-subrow">
                    <div class="bg-subrow-left">
                      <span class="subrow-label">Paired Background:</span>
                      <div *ngIf="vid.backgroundUrl" class="bg-preview-inline">
                        <img [src]="vid.backgroundUrl" alt="Background" class="preview-thumb-img-small">
                        <span class="bg-file-text">{{ extractBackgroundName(vid) }}</span>
                        <button *ngIf="!version" class="icon-action-btn danger btn-xs" (click)="deleteBackgroundForPrintingVideo(vid)" [disabled]="isUploadingVideo" title="Remove Background Image">✕</button>
                      </div>
                      <span *ngIf="!vid.backgroundUrl" class="bg-default-hint">Default Background (No image assigned)</span>
                    </div>

                    <div class="bg-subrow-right" *ngIf="!version">
                      <label class="secondary-btn btn-xs upload-btn-label">
                        <span>{{ vid.backgroundUrl ? 'Replace Image' : '+ Assign Image' }}</span>
                        <input type="file" accept="image/png, image/jpeg" (change)="uploadBackgroundForPrintingVideo($event, vid)" class="hidden-file-input" [disabled]="isUploadingVideo">
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <p *ngIf="printingVideos.length === 0" class="no-items-text">No printing videos uploaded.</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Full Page Publishing Overlay -->
  <div class="publishing-overlay" *ngIf="isPublishingDevice">
    <div class="publishing-spinner-container">
      <div class="spinner-large"></div>
      <h3 class="publishing-title">Publishing Configuration...</h3>
      <p class="publishing-desc">Please wait while the configuration is being published to {{ deviceName || deviceId }}.</p>
    </div>
  </div>

  <!-- Clone Starter Layout Modal -->
  <div class="modal-backdrop-overlay fade-in" *ngIf="isCloneModalOpen">
    <div class="clone-modal-panel">
      <div class="modal-header">
        <div class="modal-title-row">
          <span class="modal-icon">📋</span>
          <h4>Configure Standalone Layout for {{ deviceName || deviceId }}</h4>
        </div>
        <button class="modal-close-btn" (click)="closeCloneModal()" [disabled]="isCloningInProgress">✕</button>
      </div>

      <div class="modal-body">
        <p class="modal-intro-text">
          Choose a starter template for this standalone photobooth. All coordinates, themes, and assets copied will belong <strong>strictly to this device</strong> without affecting the source.
        </p>

        <!-- KAN-518 confirmation step (Copy / Replace layout) -->
        <div class="clone-confirm-panel" *ngIf="cloneConfirm" role="alert">
          <p class="clone-confirm-line" *ngFor="let line of cloneConfirm.lines; let i = index" [class.clone-confirm-warning]="cloneConfirm.replace && i === cloneConfirm.lines.length - 1">{{ line }}</p>
        </div>

        <div class="clone-options-list" *ngIf="!cloneConfirm">
          <!-- Option 0: Resume Last Saved Configuration (Shown only when standaloneTiles canvas exists — KAN-523 AC-7) -->
          <label class="clone-option-card resume-card" [class.selected]="cloneSourceSelection === 'resume'" *ngIf="hasExistingConfig">
            <input type="radio" name="cloneSource" [(ngModel)]="cloneSourceSelection" value="resume" [disabled]="isCloningInProgress">
            <div class="option-details">
              <span class="option-title">↩ Continue with Previous Device Settings (Recommended)</span>
              <span class="option-desc">Keep your existing customized canvas tiles, custom pricing, themes, and uploaded frame assets exactly as they were previously saved for this machine.</span>
            </div>
          </label>

          <!-- Option 1: Global Default Layout -->
          <label class="clone-option-card" [class.selected]="cloneSourceSelection === 'global'">
            <input type="radio" name="cloneSource" [(ngModel)]="cloneSourceSelection" value="global" [disabled]="isCloningInProgress">
            <div class="option-details">
              <span class="option-title">🌐 Clone from Global Layout</span>
              <span class="option-desc">Overwrites this machine's layout canvas with the current 1920x1080 fleet-wide global baseline coordinates and buttons.</span>
            </div>
          </label>

          <!-- Option 2: Clone from Existing Photobooth -->
          <label class="clone-option-card" [class.selected]="cloneSourceSelection === 'device'">
            <input type="radio" name="cloneSource" [(ngModel)]="cloneSourceSelection" value="device" [disabled]="isCloningInProgress">
            <div class="option-details">
              <span class="option-title">📸 Clone from Another Photobooth Machine</span>
              <span class="option-desc">Deep-clones layout tiles, all custom frame groups, and Cloud Storage artwork assets (_FP, _PP, thumbnails, backgrounds) from another machine.</span>
              
              <div class="device-selector-wrap" *ngIf="cloneSourceSelection === 'device'">
                <label class="selector-label">Select Source Machine:</label>
                <select class="custom-select source-device-select" [(ngModel)]="cloneSourceDeviceId" [disabled]="isCloningInProgress">
                  <option value="" disabled>-- Select a Photobooth Machine --</option>
                  <option *ngFor="let dev of availableCloneDevices" [value]="dev.deviceId || dev.id">
                    {{ dev.deviceName || dev.name || dev.deviceId || dev.id }} ({{ dev.deviceId || dev.id }}) — Store: {{ dev.storeId || 'N/A' }}
                  </option>
                </select>
              </div>
            </div>
          </label>

          <!-- Option 3: Blank Canvas -->
          <label class="clone-option-card" [class.selected]="cloneSourceSelection === 'blank'">
            <input type="radio" name="cloneSource" [(ngModel)]="cloneSourceSelection" value="blank" [disabled]="isCloningInProgress">
            <div class="option-details">
              <span class="option-title">📄 Start with a Blank 1920x1080 Canvas</span>
              <span class="option-desc">Starts with zero tiles, allowing you to build a completely unique layout from scratch.</span>
            </div>
          </label>
        </div>
      </div>

      <div class="modal-footer">
        <button class="secondary-btn" (click)="cloneConfirm ? cancelCloneConfirm() : closeCloneModal()" [disabled]="isCloningInProgress">Cancel</button>
        <button class="primary-btn" [class.danger-confirm-btn]="cloneConfirm?.replace" (click)="confirmCloneLayout()" [disabled]="isCloningInProgress">
          <span class="spinner-inline" *ngIf="isCloningInProgress"></span>
          <span>{{ isCloningInProgress ? 'Cloning Layout & Assets...' : (cloneConfirm ? cloneConfirm.confirmLabel : 'Apply & Unlock Standalone Layout') }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- KAN-523 AC-5: Revert to Global Confirmation Modal -->
  <div class="modal-backdrop-overlay fade-in" *ngIf="isRevertModalOpen">
    <div class="clone-modal-panel" role="dialog" aria-modal="true">
      <div class="modal-header">
        <div class="modal-title-row">
          <span class="modal-icon">↩</span>
          <h4>{{ housekeepingMessages.revertTitle }}</h4>
        </div>
        <button class="modal-close-btn" (click)="closeRevertModal()" [disabled]="isRevertingInProgress">✕</button>
      </div>
      <div class="modal-body">
        <p class="modal-intro-text">{{ housekeepingMessages.revertBody }}</p>
      </div>
      <div class="modal-footer">
        <button class="secondary-btn" (click)="closeRevertModal()" [disabled]="isRevertingInProgress">{{ housekeepingMessages.revertCancelLabel }}</button>
        <button class="primary-btn danger-confirm-btn" (click)="confirmRevertToGlobal()" [disabled]="isRevertingInProgress">
          <span class="spinner-inline" *ngIf="isRevertingInProgress"></span>
          <span>{{ housekeepingMessages.revertConfirmLabel }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- KAN-523 AC-11: Publish to Device Unsaved-Changes Warning Modal -->
  <div class="modal-backdrop-overlay fade-in" *ngIf="isUnsavedPublishModalOpen">
    <div class="clone-modal-panel" role="dialog" aria-modal="true">
      <div class="modal-header">
        <div class="modal-title-row">
          <span class="modal-icon">⚠️</span>
          <h4>Unsaved Layout Changes</h4>
        </div>
        <button class="modal-close-btn" (click)="cancelUnsavedPublishModal()">✕</button>
      </div>
      <div class="modal-body">
        <p class="modal-intro-text">{{ housekeepingMessages.publishUnsavedWarning }}</p>
      </div>
      <div class="modal-footer">
        <button class="secondary-btn" (click)="cancelUnsavedPublishModal()">{{ housekeepingMessages.publishUnsavedCancelLabel }}</button>
        <button class="primary-btn" (click)="confirmUnsavedPublishModal()">{{ housekeepingMessages.publishUnsavedConfirmLabel }}</button>
      </div>
    </div>
  </div>
</div>
